var kind                  = require("enyo/kind"),
    utils                 = require("enyo/utils"),
    CanvasControl         = require("enyo-canvas/CanvasControl"),
    EventListeningSupport = require("enyo-canvas/EventListeningSupport"),
    SimpleHitTestSupport  = require("enyo-canvas/SimpleHitTestSupport"),
    PageTransform         = require("ott/image-reader/PageTransform");

/**
 * A single page to be shown in the reader
 *
 * @class   ott.image-reader.Page
 * @extends enyo-canvas.CanvasControl
 * @public
 */
module.exports = kind(/** @lends  ott.image-reader.Page.prototype */ {

    /**
     * @private
     */
    name: "ott.image-reader.Page",

    /**
     * @private
     */
    kind: CanvasControl,

    /**
     * @private
     */
    mixins: [
        SimpleHitTestSupport,
        EventListeningSupport
    ],

    /**
     * The dataset feeding this page.
     *
     * @type {*}
     * @public
     */
    data: null,

    /**
     * @type {ott.image-reader.PageTransform}
     * @readonly
     * @public
     */
    transform: null,

    /**
     * @private
     */
    state: null,

    /**
     * @private
     */
    bindings: [{
        from: ".scene.viewport",
        to:   ".transform.viewport"
    }],

    /**
     * The page can broadcast events to request a previous or next page from the
     * reader.
     *
     * @public
     */
    events: {
        onRequestNextPage:     "",
        onRequestPreviousPage: ""
    },

    /**
     * @private
     */
    eventHandlers: {
        pointerdown:   "handlePointerDown",
        pointermove:   "handlePointerMove",
        pointerup:     "handlePointerUp",
        pointercancel: "handlePointerCancel",
        pointertap:    "handlePointerTap",
        wheel:         "handleWheel"
    },

    /**
     * @private
     */
    handlers: {
        onRequestZoomIn:  "handleRequestZoomIn",
        onRequestZoomOut: "handleRequestZoomOut"
    },

    /**
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            this.transform = new PageTransform();
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    importProps: kind.inherit(function(sup) {
        return function() {
            this.pointers            = [];
            this.pointerMovement     = {};
            this.gesture             = {};
            this.handlePointerDown   = this.bindSafely(this.handlePointerDown);
            this.handlePointerMove   = this.bindSafely(this.handlePointerMove);
            this.handlePointerUp     = this.bindSafely(this.handlePointerUp);
            this.handlePointerCancel = this.bindSafely(this.handlePointerCancel);
            this.handlePointerTap    = this.bindSafely(this.handlePointerTap);
            this.resetMultiTap       = this.bindSafely(this.resetMultiTap);
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.dataChanged();
        };
    }),

    /**
     * Reflows the transform according to the supplied multiplier. Re-applies
     * the targets to allow computed targets to be re-computed as needed.
     *
     * @param  {number}  multiplier  The multiplier
     * @public
     */
    reflow: function(multiplier) {
        this.transform.reflow(multiplier);
        this.set("dirty", true);
    },

    /**
     * Calculates the bounds of the page in canvas space.
     *
     * @return  {enyo-canvas.CanvasBounds}  The bounds
     * @public
     */
    getCanvasBounds: function() {
        return this.transform.calcBounds() || {
            x:      0,
            y:      0,
            width:  0,
            height: 0
        };
    },

    // Dataset
    // --------------------------------

    /**
     * Retrieve width and height from dataset.
     *
     * @private
     */
    dataChanged: function() {
        var data      = this.data || {},
            transform = this.transform;

        transform.set("width", data.width || 0);
        transform.set("height", data.height || 0);
    },

    // State
    // --------------------------------

    /**
     * Updates the transform and updates the saved state. If the state changed,
     * the page will automatically be marked as 'dirty', ensuring the scene will
     * re-draw.
     *
     * @public
     */
    updateState: function() {
        this.updateTransform();
        this.set("state", this.buildState());
    },

    /**
     * Updates the transform.
     *
     * @private
     */
    updateTransform: function() {
        var transform = this.transform,
            animators = transform.animators,
            positionX = animators.positionX,
            positionY = animators.positionY,
            scaleX    = animators.scaleX,
            scaleY    = animators.scaleY;

        if (!transform.canComputeTransform()) {
            return;
        }

        // Apply delta for paging gesture
        if (this.isPaging) {
            positionX.drag(this.pagingDelta);
            this.pagingDelta = 0;
        }

        // Apply delta for panning gesture
        if (this.isPanning) {
            positionX.drag(this.panningDeltaX);
            positionY.drag(this.panningDeltaY);
            this.panningDeltaX = 0;
            this.panningDeltaY = 0;
        }

        // Apply delta for zooming gesture
        if (this.isZooming) {
            scaleX.drag(this.zoomDelta);
            scaleY.drag(this.zoomDelta);
            this.zoomDelta = 0;
        }

        // Target is currently invisible and it's destination will also be
        // invisible. We can afford to simply snap the transition to it's final
        // state.
        if (!transform.isVisible() && !transform.isTargetVisible()) {
            transform.finish();
        }

        // Update the transform
        else {
            transform.update();
        }
    },

    /**
     * Makes a new snapshot of the current state of the page and stores it
     *
     * @return  {Object}  The new state
     * @private
     */
    buildState: function() {
        var transform = this.transform,
            state;

        state = {
            canTransform: transform.canTransform()
        };

        return utils.mixin(state, this.getCanvasBounds());
    },

    /**
     * Marks page as dirty if any of the state's properties changed
     *
     * @param  {?Object}  oldState  The previous state
     * @private
     */
    stateChanged: function(oldState) {
        var newState = this.get("state") || {},
            prop;

        oldState = oldState || {};

        // Mark page as dirty if state changed
        for (prop in oldState) {
            if (oldState[prop] !== newState[prop]) {
                this.set("dirty", true);

                return;
            }
        }

        for (prop in newState) {
            if (newState[prop] !== oldState[prop]) {
                this.set("dirty", true);

                return;
            }
        }
    },

    // ...........................
    // Gesturing

    /**
     * The tracked pointers are stored here.
     *
     * @type {Array}
     * @private
     */
    pointers: null,

    /**
     * Pointer movement.
     *
     * @type {Object}
     * @private
     */
    pointerMovement: null,

    /**
     * The gesture state is stored here.
     *
     * @type {Object}
     * @private
     */
    gesture: null,

    /**
     * Stop any pointer interaction as soon as the page becomes inactive.
     * Updates automatic alignment settings when page activity changes.
     *
     * @param  {boolean}  wasActive  Whether the page was active before
     * @private
     */
    activeChanged: function(wasActive) {
        var isActive = this.active;

        if (wasActive && !isActive) {
            this.isZooming && this.stopZooming();
            this.isPaging && this.stopPaging(true);
            this.isPanning && this.stopPanning();
            this.resetPointers();
        }

        if (wasActive !== isActive) {
            this.updateAutomaticAlignment();
        }
    },

    /**
     * @private
     */
    resetPointers: function() {
        var pointers        = this.pointers,
            pointerMovement = this.pointerMovement,
            pointerId;

        while (pointers.length) {
            pointerId = pointers.pop().pointerId;
            delete pointerMovement[pointerId];
            this.scene.releasePointer(pointerId, this);
        }
        this.resetMultiTap();
        this.updateGesture();
    },

    /**
     * Update the current gesture state. This should be done whenever a new
     * pointer event is received.
     *
     * @return {Object}  The gesture state
     * @private
     */
    updateGesture: function() {
        var gesture        = this.gesture,
            previousX      = gesture.x,
            previousY      = gesture.y,
            previousRadius = gesture.radius;

        gesture.x      = this.calcGestureX();
        gesture.y      = this.calcGestureY();
        gesture.radius = this.calcGestureRadius(gesture.x, gesture.y);
        gesture.deltaX = previousX || previousX === 0 ? gesture.x - previousX : 0;
        gesture.deltaY = previousY || previousY === 0 ? gesture.y - previousY : 0;
        gesture.scale  = previousRadius && gesture.radius ? gesture.radius / previousRadius : 1;

        return gesture;
    },

    /**
     * Calculates the average x-coordinate of all pointes on the canvas.
     *
     * @return {?number}  The x-coordinate, or `null` if there are no pointers.
     * @private
     */
    calcGestureX: function() {
        var pointers = this.pointers;

        return pointers.length ? pointers.reduce(function(sum, pointer) {
            return sum + pointer.canvasX;
        }, 0) / pointers.length : null;
    },

    /**
     * Calculates the average y-coordinate of all pointes on the canvas.
     *
     * @return {?number}  The y-coordinate, or `null` if there are no pointers.
     * @private
     */
    calcGestureY: function() {
        var pointers = this.pointers;

        return pointers.length ? pointers.reduce(function(sum, pointer) {
            return sum + pointer.canvasY;
        }, 0) / pointers.length : null;
    },

    /**
     * Calculates the average distance between pointers and the gesture center.
     *
     * @param  {number}   x  The x-coordinate of the gesture's center
     * @param  {number}   y  The y-coordinate of the gesture's center
     * @return {?number}  The radius, or `null` if there are no pointers
     * @private
     */
    calcGestureRadius: function(x, y) {
        var pointers = this.pointers;

        return pointers.length ? pointers.reduce(function(magnitudes, pointer) {
            return magnitudes + Math.sqrt(Math.pow(pointer.canvasX - x, 2) + Math.pow(pointer.canvasY - y, 2));
        }, 0) / pointers.length : null;
    },

    // ...........................
    // Tapping

    /**
     * The maximum amount of time in milliseconds for two-or-more taps to count
     * as a multi-tap.
     *
     * @type {number}
     * @public
     */
    multiTapTimeout: 400,

    /**
     * The multi-tap state
     *
     * @type {?Object}
     * @private
     */
    taps: null,

    /**
     * If first tap, reset multi-tap counter. Otherwise, increase tap count.
     *
     * @param  {enyo-canvas.PointerState}  pointer  The pointer
     * @private
     */
    addTap: function(pointer) {
        if (!this.taps) {
            this.taps = {
                canvasX:  pointer.canvasX,
                canvasY:  pointer.canvasY,
                tapCount: 1,
                timeout:  setTimeout(this.resetMultiTap, this.multiTapTimeout)
            };
        }
        else {
            ++this.taps.tapCount;
        }
    },

    /**
     * Reset multi-tap counter.
     *
     * @private;
     */
    resetMultiTap: function() {
        if (this.taps) {
            clearTimeout(this.taps.timeout);
            this.taps = null;
        }
    },

    // ...........................
    // Event Handlers

    /**
     * Handles one or more new tracked pointers:
     *  - Start tracking pointer
     *  - Updates gesture state
     *  - Stops any active transitions when the user starts interacting.
     *  - Stops paging interaction in case of multiple touches.
     *  - Automatically starts panning interaction in case of multiple touches.
     *  - Automatically starts zooming interaction in case of multiple touches.
     *
     * @param  {enyo-canvas.PointerState}  pointer  The pointer
     * @private
     */
    handlePointerDown: function(pointer) {
        var pointers        = this.pointers,
            pointerIndex    = pointers.indexOf(pointer),
            pointerId       = pointer.pointerId,
            pointerMovement = this.pointerMovement,
            transform       = this.transform;

        // Activate page if it isn't active already.
        if (!this.active) {
            this.pageList.activatePage(this);
        }

        // Capture new pointer
        if (pointerIndex === -1) {
            pointers.push(pointer);
            pointerMovement[pointerId] = 0;
            this.scene.capturePointer(pointerId, this);
        }

        // Update the gesture
        this.updateGesture();

        // Stop any movement and remove animation targets
        transform.stop();
        transform.clearAttractors();

        // Apply transform origin
        transform.applyOriginX(pointer.canvasX);
        transform.applyOriginY(pointer.canvasY);

        // Multiple pointers *always* means both panning and zooming.
        if (pointers.length > 1) {
            this.isPaging && this.stopPaging(true);
            !this.isPanning && this.startPanning();
            !this.isZooming && this.startZooming();
        }

        // Update settings for automatic alignment
        this.updateAutomaticAlignment();
    },

    /**
     * Handles the movement of a tracked pointer:
     *  - Prevent taps for pointers that moved too much.
     *  - Updates gesture state
     *  - Apply pointer movement to `paging` interaction if paging.
     *  - Apply pointer movement to `panning` interaction if panning.
     *  - Apply pinch & zoom gesture to `zooming` interaction if zooming.
     *
     * @param  {enyo-canvas.PointerState}  pointer  The pointer
     * @private
     */
    handlePointerMove: function(pointer) {
        var pointers  = this.pointers,
            pointerId = pointer.pointerId,
            movement  = this.pointerMovement,
            pagingDirection,
            gesture;

        // Don't interact if page isn't active or we haven't captured this
        // pointer.
        if (!this.active || pointers.indexOf(pointer) === -1) {
            return;
        }

        // Update the gesture
        gesture = this.updateGesture();

        // Increment pointer movement
        movement[pointerId] += Math.sqrt(Math.pow(pointer.dx, 2) + Math.pow(pointer.dy, 2));

        // Prevent tap for pointers that moved further than 16px.
        if (movement[pointerId] > 16 && !pointer.isTapPrevented) {
            pointer.preventTap();
        }

        // Single pointer means we should either be paging or panning.
        if (pointers.length === 1
            && !this.isPaging
            && !this.isPanning) {

            // Check if we should be paging
            pagingDirection = this.calcPagingDirection(pointer);

            // Start paging, or panning
            if (pagingDirection) {
                this.startPaging(pagingDirection);
            }
            else {
                this.startPanning();
            }

            // Update auto-alignment
            this.updateAutomaticAlignment();
        }

        // Apply paging as needed
        if (this.isPaging) {
            this.paging(pointer.dx);
        }

        // Apply panning as needed
        if (this.isPanning) {
            this.panning(pointer.dx, pointer.dy);
        }

        // Apply zooming as needed
        if (this.isZooming) {
            this.zooming(gesture.scale - 1);
        }
    },

    /**
     * Handles the release of one or more tracked pointers:
     *  - Stops tracking pointer
     *  - Updates gesture state
     *  - Stops `zooming` interaction when pointer count becomes less than 2
     *  - Stops `panning` or `paging` interaction when pointers count becomes
     *    less than 1
     *
     * @param  {enyo-canvas.PointerState}  pointer  The pointer
     * @private
     */
    handlePointerUp: function(pointer) {
        var pointers  = this.pointers,
            pointerId = pointer.pointerId,
            pointerIndex;

        // Don't interact with inactive page
        if (!this.active) {
            return;
        }

        pointerIndex = pointers.indexOf(pointer);

        // Don't interact with pointers we haven't captured
        if (pointerIndex === -1) {
            return;
        }

        // Unregister pointer
        pointers.splice(pointerIndex, 1);
        delete this.pointerMovement[pointerId];
        this.scene.releasePointer(pointerId, this);

        // Update the gesture
        this.updateGesture();

        // Zooming requires 2 or more pointers
        if (pointers.length < 2) {
            this.isZooming && this.stopZooming();
        }

        // Paging or panning requires at least one pointer
        if (pointers.length < 1) {
            this.isPaging && this.stopPaging();
            this.isPanning && this.stopPanning();
        }

        // Update auto-alignment
        this.updateAutomaticAlignment();
    },

    /**
     * Handles the cancellation of one or more tracked pointers:
     *  - Stops tracking pointer
     *  - Updates gesture state
     *  - Stops `zooming` interaction when pointer count becomes less than 2
     *  - Stops `panning` or `paging` interaction when pointers count becomes
     *    less than 1
     *
     * @param  {enyo-canvas.PointerState}  pointer  The pointer
     * @private
     */
    handlePointerCancel: function(pointer) {
        var pointers  = this.pointers,
            pointerId = pointer.pointerId,
            pointerIndex;

        // Don't interact with inactive page
        if (!this.active) {
            return;
        }

        pointerIndex = pointers.indexOf(pointer);

        // Don't interact with pointers we haven't captured
        if (pointerIndex === -1) {
            return;
        }

        // Unregister pointer
        pointers.splice(pointerIndex, 1);
        delete this.pointerMovement[pointerId];
        this.scene.releasePointer(pointerId, this);

        // Update the gesture
        this.updateGesture();

        // Zooming requires 2 or more pointers
        if (pointers.length < 2) {
            this.isZooming && this.stopZooming();
        }

        // Paging or panning requires at least one pointer
        if (pointers.length < 1) {
            this.isPaging && this.stopPaging(true);
            this.isPanning && this.stopPanning();
        }

        // Update auto-alignment
        this.updateAutomaticAlignment();
    },

    /**
     * Use double tap to zoom in / out if the user is not gesturing with any
     * pointers. Double-tap zooms in until max zoom is reached, then it zooms
     * out again until min zoom is reached.
     *
     * @param  {enyo-canvas.PointerState}  pointer  The pointer
     * @private
     */
    handlePointerTap: function(pointer) {
        var transform     = this.transform,
            scaleX        = transform.animators.scaleX,
            zoomDirection = this.zoomDirection,
            currentScale,
            scales,
            i,
            n,
            targetScale;

        // Don't interact if page isn't active or we haven't captured this
        // pointer.
        if (!this.active) {
            return;
        }

        // Only zoom on double-tap
        this.addTap(pointer);

        // Double-tap required in order to zoom
        if (this.taps.tapCount < 2) {
            return;
        }

        // Initiate zoom as needed
        if (!this.isPaging && !this.isPanning && !this.isZooming) {

            // Get current target scale
            currentScale = scaleX.attractors.target.value;

            // Or fall back to current scale
            if (currentScale === null) {
                currentScale = scaleX.scalar.value;
            }

            // Unique set of scales to snap to
            scales = [
                transform.calcContainScale(),
                transform.calcCoverScale(),
                transform.calcMaxScale()
            ].filter(function(value, index, self) {
                return self.indexOf(value) === index;
            });

            // Only one scale to snap to
            if (scales.length === 1) {
                targetScale = scales[0];
            }

            // Zoom out
            else if (zoomDirection === "out") {

                // Zoom to next scale smaller than the current scale
                for (i = scales.length - 1, n = 0; i >= n; i--) {
                    if (currentScale > scales[i]) {
                        targetScale = scales[i];
                        break;
                    }
                }

                // There is no smaller scale than the current scale, which means
                // we're at min scale. Instead, let's zoom in again.
                if (!targetScale) {
                    targetScale = scales[1];
                }
            }

            // Zoom in
            else {

                // Zoom to next scale larger than the current scale
                for (i = 0, n = scales.length; i < n; i++) {
                    if (currentScale < scales[i]) {
                        targetScale = scales[i];
                        break;
                    }
                }

                // There is no larger scale than the current scale, which means
                // we're at max scale. Instead, let's zoom out again.
                if (!targetScale) {
                    targetScale = scales[scales.length - 2];
                }
            }

            // If we have a new target, apply it
            if (targetScale) {

                // Apply transform origin
                transform.applyOriginX(pointer.canvasX);
                transform.applyOriginY(pointer.canvasY);

                // Set new scale target
                transform.set("targetScaleX", targetScale);
                transform.set("targetScaleY", targetScale);

                // Remember zooming direction
                this.zoomDirection = targetScale > currentScale ? "in" : "out";

                // Auto-center if target scale <= containScale
                if (targetScale <= transform.calcContainScale()) {
                    transform.set("targetPositionX", "center", true);
                    transform.set("targetPositionY", "center", true);
                }

                // Otherwise we keep the page at current position
                else {
                    transform.animators.positionX.stop();
                    transform.animators.positionY.stop();
                    transform.animators.positionX.clearAttractors();
                    transform.animators.positionY.clearAttractors();
                }
            }
        }

        // Reset multi-count and wait for next double-tap.
        this.resetMultiTap();
    },

    /**
     * Use wheel delta to zoom in/out if the user is not gesturing with any
     * pointers.
     *
     * @param  {enyo-canvas.WheelState}  wheel  The wheel
     * @private
     */
    handleWheel: function(wheel) {
        var transform = this.transform,
            scaleX    = transform.animators.scaleX,
            step,
            wheelDelta,
            currentScale,
            targetScale;

        // Don't interact with inactive page
        if (!this.active) {
            return;
        }

        if (!this.isPaging && !this.isPanning && !this.isZooming) {

            // Calculate how much we should zoom in / out.
            step = (transform.calcMaxScale() - transform.calcMinScale()) / 10;

            // Get wheel delta
            wheelDelta = wheel.deltaY || 0;

            // Get current target scale
            currentScale = scaleX.attractors.target.value;

            // Or fall back to current scale
            if (currentScale === null) {
                currentScale = scaleX.scalar.value;
            }

            // Calculate new scale
            if (wheelDelta < 0) {
                targetScale = currentScale + step;
            }
            else if (wheelDelta > 0) {
                targetScale = currentScale - step;
            }
            else {
                return;
            }

            // Apply transform origin
            transform.applyOriginX(wheel.canvasX);
            transform.applyOriginY(wheel.canvasY);

            // Set new scale target
            transform.set("targetScaleX", targetScale);
            transform.set("targetScaleY", targetScale);

            // Remember zooming direction
            this.zoomDirection = targetScale > currentScale ? "in" : "out";

            // Auto-center if target scale <= containScale
            if (targetScale <= transform.calcContainScale()) {
                transform.set("targetPositionX", "center", true);
                transform.set("targetPositionY", "center", true);
            }

            // Otherwise we keep the page at current position
            else {
                transform.animators.positionX.stop();
                transform.animators.positionY.stop();
                transform.animators.positionX.clearAttractors();
                transform.animators.positionY.clearAttractors();
            }
        }
    },

    /**
     * Handle request to zoom in.
     *
     * @return  {boolean}  Stop propagation
     * @public
     */
    handleRequestZoomIn: function() {
        var transform = this.transform,
            viewport  = this.scene.viewport,
            scaleX    = transform.animators.scaleX,
            currentScale,
            step,
            targetScale;

        // Don't interact with inactive page
        if (!this.active) {
            return false;
        }

        // Get current target scale
        currentScale = scaleX.attractors.target.value;

        // Or fall back to current scale
        if (currentScale === null) {
            currentScale = scaleX.scalar.value;
        }

        // Calculate new scale
        step        = (transform.calcMaxScale() - transform.calcMinScale()) / 3;
        targetScale = currentScale + step;

        // Set transform origin to viewport center
        transform.applyOriginX(viewport.width / 2);
        transform.applyOriginY(viewport.height / 2);

        // Apply zoom target
        transform.set("targetScaleX", targetScale);
        transform.set("targetScaleY", targetScale);

        // Remember zooming direction
        this.zoomDirection = "in";

        return true;
    },

    /**
     * Handle request to zoom out.
     *
     * @return  {boolean}  Stop propagation
     * @public
     */
    handleRequestZoomOut: function() {
        var transform = this.transform,
            viewport  = this.scene.viewport,
            scaleX    = transform.animators.scaleX,
            currentScale,
            step,
            targetScale;

        // Don't interact with inactive page
        if (!this.active) {
            return false;
        }

        // Get current target scale
        currentScale = scaleX.attractors.target.value;

        // Or fall back to current scale
        if (currentScale === null) {
            currentScale = scaleX.scalar.value;
        }

        // Calculate new scale
        step        = (transform.calcMaxScale() - transform.calcMinScale()) / 3;
        targetScale = currentScale - step;

        // Set transform origin to viewport center
        transform.applyOriginX(viewport.width / 2);
        transform.applyOriginY(viewport.height / 2);

        // Apply zoom target
        transform.set("targetScaleX", targetScale);
        transform.set("targetScaleY", targetScale);

        // Remember zooming direction
        this.zoomDirection = "out";

        // Auto-center if target scale <= containScale
        if (targetScale <= transform.calcContainScale()) {
            transform.set("targetPositionX", "center", true);
            transform.set("targetPositionY", "center", true);
        }

        return true;
    },

    // ...........................
    // Paging interaction

    /**
     * Whether the user is currently trying to drag to a different page.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    isPaging: false,

    /**
     * The allowed direction of the paging gesture.
     *
     * Allowed values are:
     *  - `forward` (drag to next page only)
     *  - `backward` (drag to previous page only)
     *  - `bidirectional` (drag to either next or previous page)
     *
     * @type    {?string}
     * @default null
     * @public
     */
    pagingDirection: null,

    /**
     * The sum of all horizontal drag delta's received during the paging
     * gesture.
     *
     * @type    {number}
     * @default 0
     * @public
     */
    pagingDistance: 0,

    /**
     * The sum of all drag delta's received during the current frame.
     *
     * @type    {number}
     * @default 0
     * @public
     */
    pagingDelta: 0,

    /**
     * Detect the direction of a paging gesture.
     *
     * @param  {enyo-canvas.PointerState}  pointer  The pointer
     * @return {?string}  The paging direction, or `null` if conditions for
     *                    paging are not met
     * @private
     */
    calcPagingDirection: function(pointer) {
        var transform    = this.transform,
            pageList     = this.pageList,
            containScale = transform.calcContainScale(),
            index        = pageList.getIndexByRecord(this.data),
            isFirst      = index === 0 ? true : false,
            isLast       = index === (pageList.length - 1) ? true : false,
            scaleX       = transform.animators.scaleX.scalar.value,
            distanceLeft,
            distanceRight,
            deltaX;

        // Only allow paging with 1 pointer on canvas
        if (this.pointers.length > 1) {
            return null;
        }

        // If page fits inside the viewport, it always has a paging direction.
        if (scaleX <= containScale) {
            if (isFirst) {
                return "forward";
            }
            else if (isLast) {
                return "backward";
            }
            else {
                return "bidirectional";
            }
        }

        // Check for unidirectional paging
        else {

            // Calculate overflow, remove decimals to avoid rounding issues
            distanceLeft  = ~~transform.calcDistanceFromLeft();
            distanceRight = ~~transform.calcDistanceFromRight();
            deltaX        = pointer.dx;

            // Paging forward is possible
            if (deltaX < 0 && !isLast) {

                // Page is as far left as it can be while still having as much
                // of the page visible as possible.
                if ((distanceLeft === 0 && distanceRight < 0)
                    || (distanceRight === 0 && distanceLeft < 0)) {
                    return "forward";
                }
            }

            // Paging backward is possible
            if (deltaX > 0 && !isFirst) {

                // Page is as far right as it can be while still having as much
                // of the page visible as possible.
                if ((distanceRight === 0 && distanceLeft > 0)
                    || (distanceLeft === 0 && distanceRight > 0)) {
                    return "backward";
                }
            }
        }

        return null;
    },

    /**
     * Starts the paging gesture by resetting it's metrics and marking the page
     * as `isPaging`.
     *
     * @param {string}  direction  The allowed paging direction
     * @public
     */
    startPaging: function(direction) {
        if (this.isPaging) {
            return;
        }

        // Reset metrics
        this.pagingDirection = direction;
        this.pagingDistance  = 0;
        this.pagingDelta     = 0;

        // Enable paging interaction
        this.set("isPaging", true);
    },

    /**
     * Drags the page about according to the incoming drag delta.
     *
     * @param {number}  deltaX  The amount of pixels dragged along the x-axis
     * @public
     */
    paging: function(deltaX) {
        if (!this.isPaging) {
            return;
        }

        // Unset target
        this.transform.set("targetPositionX", null);

        // Apply paging
        this.pagingDelta += deltaX;

        // Keep track of total paging distance
        this.pagingDistance += deltaX;
    },

    /**
     * Finalizes the paging gesture based on distance, velocity and direction of
     * the gesture.
     *
     * If the dragged distance or drag velocity are sufficiently large, we
     * request a transition to the next or previous page.
     *
     * @param {boolean=}  preventPaging  Optionally prevent paging
     * @public
     */
    stopPaging: function(preventPaging) {
        var transform    = this.transform,
            velocity     = transform.animators.positionX.scalar.velocity,
            direction    = this.pagingDirection,
            distance     = this.pagingDistance,
            scaleX       = transform.animators.scaleX,
            containScale = transform.calcContainScale(),
            targetPage   = null,
            currentScale,
            distanceThreshold,
            speedThreshold;

        // Not paging
        if (!this.isPaging) {
            return;
        }

        // Evaluate distance and speed to request a pagination.
        if (!preventPaging) {

            // Move more than half the distance of a fitting page to flick to
            // next or previous page
            distanceThreshold = transform.width * containScale * 0.5;

            // Move more than 16 pixels per frame
            speedThreshold = 16;

            // Forward paging
            if (distance < 0
                && (direction === "forward"
                    || direction === "bidirectional")
                && (distance <= -distanceThreshold
                    || velocity <= -speedThreshold)
            ) {
                targetPage = "next";
            }

            // Backward paging
            else if (distance > 0
                && (direction === "backward"
                    || direction === "bidirectional")
                && (distance >= distanceThreshold
                    || velocity >= speedThreshold)
            ) {
                targetPage = "previous";
            }
        }

        // If the gesture didn't meet the threshold for navigating to another
        // page, we may still want to animate the page back to a centered
        // position.
        if (targetPage === null) {
            // Get current target scale
            currentScale = scaleX.attractors.target.value;

            // Or fall back to current scale
            if (currentScale === null) {
                currentScale = scaleX.scalar.value;
            }

            // The page fits entirely within the viewport. Clearing a target
            // position will allow automatic alignment to animate it back to
            // centered position.
            if (currentScale <= containScale) {
                transform.set("targetPositionX", null);
            }
        }

        // Clear paging metrics
        this.pagingDirection = null;
        this.pagingDistance  = 0;
        this.pagingDelta     = 0;

        // Transitioning to another page will cause this page to get
        // deactivated. Upon deactivation this page will check for active
        // `paging` gesture and call `stopPaging` if it finds one. To prevent
        // firing `stopPaging` twice, we mark the current gesture as stopped
        // before transitioning to another page.
        this.set("isPaging", false);

        // Execute the transition
        switch (targetPage) {
            case "next":
                this.doRequestNextPage();
                break;
            case "previous":
                this.doRequestPreviousPage();
                break;
            default:
                break;
        }
    },

    // ...........................
    // Panning interaction

    /**
     * Whether the user is currently panning the page.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    isPanning: false,

    /**
     * The sum of all horizontal drag delta's received during the current frame.
     *
     * @type    {number}
     * @default 0
     * @public
     */
    panningDeltaX: 0,

    /**
     * The sum of all vertical drag delta's received during the current frame.
     *
     * @type    {number}
     * @default 0
     * @public
     */
    panningDeltaY: 0,

    /**
     * Starts the paging gesture by marking the page as `isPanning`.
     *
     * @public
     */
    startPanning: function() {
        if (this.isPanning) {
            return;
        }

        // Reset metrics
        this.panningDeltaX = 0;
        this.panningDeltaY = 0;

        // Enable panning interaction
        this.set("isPanning", true);
    },

    /**
     * Drags the page according to the incoming drag delta.
     *
     * @param {number}  deltaX  The amount of pixels dragged along the x-axis
     * @param {number}  deltaY  The amount of pixels dragged along the y-axis
     * @public
     */
    panning: function(deltaX, deltaY) {
        var transform = this.transform;

        // Not panning
        if (!this.isPanning) {
            return;
        }

        // Unset target
        transform.set("targetPositionX", null);
        transform.set("targetPositionY", null);

        // Apply panning
        this.panningDeltaX += deltaX;
        this.panningDeltaY += deltaY;
    },

    /**
     * Stops the panning gesture
     *
     * @public
     */
    stopPanning: function() {

        // Not panning
        if (!this.isPanning) {
            return;
        }

        // Clear panning metrics
        this.panningDeltaX = 0;
        this.panningDeltaY = 0;

        // Stop panning interaction
        this.set("isPanning", false);
    },

    // ...........................
    // Scaling interaction

    /**
     * Whether the user is currently zooming the page
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    isZooming: false,

    /**
     * The current direction of the zooming transition
     *
     * Allowed values are:
     *  - `in` when zooming in.
     *  - `out` when zooming out.
     *
     * @type    {?string}
     * @default null
     * @public
     */
    zoomDirection: null,

    /**
     * The sum of all scaling delta's received during the current frame.
     *
     * @type    {number}
     * @default 0
     * @public
     */
    zoomDelta: 0,

    /**
     * Starts the zooming gesture by marking the page as `isZooming`.
     *
     * @public
     */
    startZooming: function() {
        if (this.isZooming) {
            return;
        }

        // Reset metrics
        this.zoomDirection = null;
        this.zoomDelta     = 0;

        // Enable zooming interaction
        this.set("isZooming", true);
    },

    /**
     * Sets the transform origin to the supplied coordinates and increases or
     * decreases the scale accordingly.
     *
     * @param  {number}  delta  The difference in scale factor
     * @private
     */
    zooming: function(delta) {
        var transform = this.transform;

        // Not zooming
        if (!this.isZooming) {
            return;
        }

        // Unset target
        transform.set("targetScaleX", null);
        transform.set("targetScaleY", null);

        // Apply panning
        this.zoomDelta += delta;

        // Remember zooming direction
        this.zoomDirection = delta > 0 ? "in" : "out";
    },

    /**
     * Stops the zooming gesture
     *
     * @public
     */
    stopZooming: function() {

        // Not zooming
        if (!this.isZooming) {
            return;
        }

        // Clear zooming metrics
        this.zoomDirection = null;
        this.zoomDelta     = 0;

        // Stop zooming interaction
        this.set("isZooming", false);
    },

    // ...........................
    // Automatic Alignment

    /**
     * Apply automatic overflow reduction and automatic centering of the page
     * depending on the current user interactions.
     *
     * @public
     */
    updateAutomaticAlignment: function() {
        var transform       = this.transform,
            isPaging        = this.isPaging,
            isPanning       = this.isPanning,
            isZooming       = this.isZooming,
            pagingDirection = this.pagingDirection,
            numPointers     = this.pointers.length;

        // Inactive pages don't auto-align
        if (!this.active) {
            transform.reduceOverflowLeft   = false;
            transform.reduceOverflowRight  = false;
            transform.reduceOverflowTop    = false;
            transform.reduceOverflowBottom = false;
            transform.autoCenterX          = false;
            transform.autoCenterY          = false;

            return;
        }

        // Panning and / or Zooming (One or more pointers):
        // - Prevent increasing overflow.
        // - Don't auto-center
        if (isPanning || isZooming) {
            transform.reduceOverflowLeft   = "guide";
            transform.reduceOverflowRight  = "guide";
            transform.reduceOverflowTop    = "guide";
            transform.reduceOverflowBottom = "guide";
            transform.autoCenterX          = false;
            transform.autoCenterY          = false;

            return;
        }

        // Paging (Single pointer):
        // - Prevent increasing overflow in non-paging direction.
        // - Allow overflow in paging direction.
        // - Don't auto-center
        if (isPaging) {
            transform.reduceOverflowLeft   = pagingDirection === "backward" ? "guide" : false;
            transform.reduceOverflowRight  = pagingDirection === "forward" ? "guide" : false;
            transform.reduceOverflowTop    = "guide";
            transform.reduceOverflowBottom = "guide";
            transform.autoCenterX          = false;
            transform.autoCenterY          = false;

            return;
        }

        // Idle pointers:
        // - Don't reduce overflow.
        // - Don't auto-center.
        if (numPointers) {
            transform.reduceOverflowLeft   = false;
            transform.reduceOverflowRight  = false;
            transform.reduceOverflowTop    = false;
            transform.reduceOverflowBottom = false;
            transform.autoCenterX          = false;
            transform.autoCenterY          = false;

            return;
        }

        // Fully idle:
        // - Animate towards a position with as little overflow as possible.
        // - Animate the page to a centered position.
        if (!numPointers) {
            transform.reduceOverflowLeft   = "animate";
            transform.reduceOverflowRight  = "animate";
            transform.reduceOverflowTop    = "animate";
            transform.reduceOverflowBottom = "animate";
            transform.autoCenterX          = "animate";
            transform.autoCenterY          = "animate";
        }
    }

});
